import React, { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { ApplicationPaymentModel } from '@/models/application.model'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { paymentsService, PaymentVerificationResult } from '@/services/paymentsService'
import { toast } from 'react-toastify'

interface Props {
  isOpen: boolean
  onClose: () => void
  applicationId: string
  payment: ApplicationPaymentModel | null
}

export const ApplicationPaymentVerificationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  applicationId,
  payment,
}) => {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<'confirmed' | 'rejected' | ''>('')
  const [note, setNote] = useState('')

  useEffect(() => {
    if (isOpen) {
      setStatus('')
      setNote('')
    }
  }, [isOpen, payment])

  const mutation = useMutation({
    mutationFn: (payload: { status: 'confirmed' | 'rejected'; note?: string }) =>
      paymentsService.verifyPaymentProof(payment?.id ?? '', payload),
    onSuccess: (res: PaymentVerificationResult) => {
      const allocationLabel =
        res.allocation?.totalAmount !== undefined && res.allocation?.totalPaid !== undefined
          ? `Paid ${res.allocation.totalPaid.toLocaleString()} of ${res.allocation.totalAmount.toLocaleString()}`
          : null
      const statusLabel = res.allocation?.newStatus ? `Status: ${res.allocation.newStatus}` : null
      const summary = [allocationLabel, statusLabel].filter(Boolean).join(' • ')
      toast.success(
        `Payment ${res.action ?? 'updated'}${summary ? ` (${summary})` : ''}`,
      )
      queryClient.invalidateQueries({ queryKey: ['application', applicationId] })
      onClose()
      return res
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Unable to verify payment')
    },
  })

  const handleSubmit = () => {
    if (!payment?.id) {
      toast.error('Select a payment to verify')
      return
    }

    if (!status) {
      toast.error('Select a verification status')
      return
    }

    mutation.mutate({
      status,
      note: note.trim() ? note.trim() : undefined,
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Verify Payment Proof"
      size="sm"
    >
      {payment ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 p-3 bg-gray-50 text-sm text-gray-700">
            <p className="font-semibold text-gray-900">{payment.reference}</p>
            <p>
              Amount:{' '}
              <span className="font-medium">
                {payment.currency ?? 'NGN'}{' '}
                {payment.amount !== null
                  ? payment.amount.toLocaleString()
                  : '—'}
              </span>
            </p>
            <p>Status: {payment.status ?? 'pending'}</p>
            {payment.receipt_url && (
              <p className="mt-1">
                Receipt:{' '}
                <a
                  href={payment.receipt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline"
                >
                  View evidence
                </a>
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Verification status
            </label>
            <select
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#7b2a40]"
              value={status}
              onChange={(event) =>
                setStatus(event.target.value as 'confirmed' | 'rejected' | '')
              }
            >
              <option value="">Select status</option>
              <option value="confirmed">Confirm payment</option>
              <option value="rejected">Reject payment</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note (optional)
            </label>
            <textarea
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#7b2a40] min-h-[80px]"
              placeholder="Add context for this decision"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="w-full bg-[#7b2a40] text-white py-2 rounded-md text-sm font-medium hover:bg-[#682133] transition disabled:opacity-70"
          >
            {mutation.isPending ? 'Saving…' : 'Update Verification'}
          </button>
        </div>
      ) : (
        <p className="text-sm text-gray-600">
          Select a payment record to verify.
        </p>
      )}
    </Modal>
  )
}
